// src/components/DatasheetManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebaseConfig";

const EMPTY_DATASHEET = {
  code: "",
  name: "",
  description: "",
  category: "product",
  tags: [],
  assets: {
    sharepointFolderId: "",
    sharepointFolderUrl: "",
    fileUrl: "",
  },
  createdAt: null,
  updatedAt: null,
};

export default function DatasheetManager() {
  const [datasheets, setDatasheets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState("idle"); // idle | create | edit | copy

  const [form, setForm] = useState(EMPTY_DATASHEET);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const [localFile, setLocalFile] = useState(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [uploading, setUploading] = useState(false);

  const spCreateDatasheetFolder = useMemo(
    () => httpsCallable(functions, "spCreateDatasheetFolder"),
    []
  );
  const spCreateUploadSession = useMemo(
    () => httpsCallable(functions, "spCreateUploadSession"),
    []
  );

  // Load datasheets
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const q = query(collection(db, "datasheets"), orderBy("name"));
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDatasheets(rows);
      } catch (e) {
        console.error(e);
        setSaveError("Failed to load datasheets.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = !search.trim()
    ? datasheets
    : datasheets.filter((d) => {
        const s = search.toLowerCase();
        return (
          String(d.code || d.id || "")
            .toLowerCase()
            .includes(s) ||
          String(d.name || "")
            .toLowerCase()
            .includes(s) ||
          String(d.category || "")
            .toLowerCase()
            .includes(s)
        );
      });

  function normalise(ds) {
    return {
      ...EMPTY_DATASHEET,
      ...ds,
      assets: { ...EMPTY_DATASHEET.assets, ...(ds.assets || {}) },
    };
  }

  function onSelect(id) {
    const ds = datasheets.find((d) => d.id === id);
    if (!ds) return;
    setSelectedId(id);
    setMode("edit");
    setForm(normalise(ds));
    setLocalFile(null);
    setInfoMessage("");
    setSaveError("");
  }

  function startNew() {
    setSelectedId(null);
    setMode("create");
    setForm(EMPTY_DATASHEET);
    setLocalFile(null);
    setInfoMessage("");
    setSaveError("");
  }

  function copySelected() {
    if (!selectedId) {
      setSaveError("Select a datasheet to copy.");
      return;
    }
    const ds = datasheets.find((d) => d.id === selectedId);
    if (!ds) return;

    const copy = normalise(ds);
    copy.code = "";
    copy.name = ds.name ? `${ds.name} (Copy)` : "";
    copy.assets = {
      sharepointFolderId: "",
      sharepointFolderUrl: "",
      fileUrl: "",
    };

    setSelectedId(null);
    setMode("copy");
    setForm(copy);
    setLocalFile(null);
    setInfoMessage("");
    setSaveError("");
  }

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setAssets(partial) {
    setForm((prev) => ({
      ...prev,
      assets: { ...prev.assets, ...partial },
    }));
  }

  async function save(e) {
    e?.preventDefault();
    setSaveError("");
    setInfoMessage("");

    if (!form.code.trim()) {
      setSaveError("Code is required.");
      return;
    }

    const code = form.code.trim().toUpperCase();
    const ref = doc(db, "datasheets", code);

    const tagsArray = Array.isArray(form.tags)
      ? form.tags
      : String(form.tags || "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);

    const payload = {
      ...form,
      code,
      tags: tagsArray,
      updatedAt: serverTimestamp(),
    };
    if (!payload.createdAt) payload.createdAt = serverTimestamp();

    try {
      setIsSaving(true);

      await setDoc(ref, payload, { merge: true });

      setDatasheets((prev) => {
        const idx = prev.findIndex((d) => d.id === code);
        const row = { id: code, ...payload };
        if (idx === -1) {
          return [...prev, row].sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""))
          );
        }
        const copy = [...prev];
        copy[idx] = row;
        return copy.sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        );
      });

      setSelectedId(code);
      setMode("edit");
      setInfoMessage("Datasheet saved.");
    } catch (err) {
      console.error(err);
      setSaveError("Failed to save datasheet.");
    } finally {
      setIsSaving(false);
    }
  }

  async function ensureFolder() {
    if (!form.code.trim()) {
      setSaveError("Save a code first, then create a folder.");
      return;
    }

    if (form.assets.sharepointFolderId) {
      setInfoMessage("Folder already linked – reusing existing.");
      return {
        folderItemId: form.assets.sharepointFolderId,
        folderWebUrl: form.assets.sharepointFolderUrl,
      };
    }

    try {
      setCreatingFolder(true);
      setSaveError("");
      setInfoMessage("");

      const res = await spCreateDatasheetFolder({ code: form.code.trim() });
      const { folderItemId, folderWebUrl } = res.data || {};
      if (!folderItemId || !folderWebUrl) {
        throw new Error("spCreateDatasheetFolder did not return folderItemId/webUrl.");
      }

      const newAssets = {
        ...form.assets,
        sharepointFolderId: folderItemId,
        sharepointFolderUrl: folderWebUrl,
      };
      setAssets(newAssets);

      await setDoc(
        doc(db, "datasheets", form.code.trim()),
        {
          assets: newAssets,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setInfoMessage("SharePoint folder created/linked.");
      return { folderItemId, folderWebUrl };
    } catch (err) {
      console.error(err);
      setSaveError(err.message || "Failed to create folder.");
      throw err;
    } finally {
      setCreatingFolder(false);
    }
  }

async function uploadPdf() {
  if (!localFile) return;
  if (!form.code.trim()) {
    setSaveError("Save a code first, then upload.");
    return;
  }

  try {
    setUploading(true);
    setSaveError("");
    setInfoMessage("");

    // Ensure/obtain SharePoint folder
    const { folderItemId } = await ensureFolder();

    // Create upload session via callable
    const sessionRes = await spCreateUploadSession({
      folderItemId,
      fileName: localFile.name,
    });
    const { uploadUrl } = sessionRes.data || {};
    if (!uploadUrl) throw new Error("No uploadUrl returned.");

    // ---- This is the important bit ----
    const size = localFile.size;
    const end = size - 1;

    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": localFile.type || "application/pdf",
        "Content-Length": String(size),
        "Content-Range": `bytes 0-${end}/${size}`,
      },
      body: localFile,
    });

    if (!putRes.ok) {
      const text = await putRes.text().catch(() => "");
      throw new Error(`Upload failed: ${putRes.status} ${text}`);
    }

    const driveItem = await putRes.json();
    const webUrl = driveItem?.webUrl;
    if (!webUrl) throw new Error("Upload succeeded but no webUrl returned.");

    const newAssets = {
      ...form.assets,
      fileUrl: webUrl,
    };
    setAssets(newAssets);

    await setDoc(
      doc(db, "datasheets", form.code.trim()),
      {
        assets: newAssets,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    setInfoMessage("PDF uploaded and linked.");
  } catch (err) {
    console.error(err);
    setSaveError(err.message || "Failed to upload PDF.");
  } finally {
    setUploading(false);
  }
}


  const modeLabel =
    mode === "create"
      ? "Creating datasheet"
      : mode === "copy"
      ? "Copying datasheet"
      : mode === "edit"
      ? `Editing ${form.code || selectedId || ""}`
      : "Select or create a datasheet";

  const hasFolder = !!form.assets.sharepointFolderId;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Datasheets</h1>

        <button
          type="button"
          onClick={startNew}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
        >
          New datasheet
        </button>
        <button
          type="button"
          onClick={copySelected}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
        >
          Copy selected
        </button>

        <span className="ml-auto text-sm text-gray-500">
          {isSaving
            ? "Saving…"
            : mode === "idle"
            ? "No datasheet selected"
            : "Ready"}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-4 h-max bg-white border border-gray-200 rounded-xl p-3">
          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">
              Search datasheets
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
              placeholder="Code, name, category…"
            />
          </div>

          <div className="border-t border-gray-200 pt-2 max-h-[400px] overflow-auto text-sm">
            {loading ? (
              <div className="text-gray-500 text-sm p-2">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-gray-500 text-sm p-2">
                No datasheets found.
              </div>
            ) : (
              <ul className="space-y-1">
                {filtered.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(d.id)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                        selectedId === d.id
                          ? "bg-indigo-600 text-white"
                          : "hover:bg-gray-100 text-gray-800"
                      }`}
                    >
                      <div className="font-medium truncate">
                        {d.name || "Untitled datasheet"}
                      </div>
                      <div className="text-xs text-gray-200 lg:text-gray-300">
                        {d.code || d.id} {d.category ? `· ${d.category}` : ""}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-4 p-3 bg-gray-50 border border-dashed rounded-md text-xs text-gray-600">
            <div>
              <b>Mode:</b> {modeLabel}
            </div>
            <div>
              <b>Count:</b> {datasheets.length}
            </div>
          </div>
        </aside>

        {/* Main form */}
        <form onSubmit={save} className="space-y-8">
          {/* Metadata section */}
          <section className="bg-white border border-gray-200 rounded-xl p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Datasheet details
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Code, name, category, tags and description.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) =>
                    setField("code", e.target.value.toUpperCase())
                  }
                  placeholder="TEMP-UNV-DS01"
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Used as Firestore document ID and SharePoint folder name.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="Tempest Unvented Cylinders Datasheet"
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setField("category", e.target.value)}
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                >
                  <option value="product">Product</option>
                  <option value="part">Part</option>
                  <option value="erp">ERP label</option>
                  <option value="install">Installation guide</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  value={
                    Array.isArray(form.tags)
                      ? form.tags.join(", ")
                      : form.tags || ""
                  }
                  onChange={(e) => setField("tags", e.target.value)}
                  placeholder="tempest, unvented, domestic"
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  placeholder="Short description of what this datasheet covers."
                  className="w-full text-sm rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
            </div>
          </section>

          {/* SharePoint / PDF section */}
          <section className="bg-white border border-gray-200 rounded-xl p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              SharePoint & PDF
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Create or link a SharePoint folder and upload the main PDF.
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
              <div className="text-gray-700">
                <b>Folder status:</b>{" "}
                {hasFolder ? "Linked to SharePoint" : "No folder yet"}
              </div>
              {form.assets.sharepointFolderUrl && (
                <a
                  href={form.assets.sharepointFolderUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 hover:text-indigo-700 text-sm"
                >
                  Open folder
                </a>
              )}
              <button
                type="button"
                onClick={ensureFolder}
                disabled={!form.code.trim() || creatingFolder}
                className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm disabled:opacity-60"
              >
                {creatingFolder
                  ? "Creating folder…"
                  : hasFolder
                  ? "Ensure / re-link folder"
                  : "Create folder"}
              </button>
            </div>

            <div className="grid sm:grid-cols-[minmax(0,1fr)_auto] gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Datasheet PDF
                </label>
                <input
                  type="file"
                  accept="application/pdf"
                  disabled={!form.code.trim()}
                  onChange={(e) => setLocalFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border file:border-gray-300 file:text-sm file:font-medium file:bg-white file:text-gray-700 hover:file:bg-gray-50"
                />
                {form.assets.fileUrl && (
                  <div className="mt-2">
                    <a
                      href={form.assets.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 hover:text-indigo-700 text-sm"
                    >
                      View current PDF
                    </a>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={uploadPdf}
                disabled={!localFile || uploading}
                className="px-4 py-2 rounded-md border border-gray-300 bg-indigo-600 text-white hover:bg-indigo-700 text-sm disabled:opacity-60"
              >
                {uploading ? "Uploading…" : "Upload PDF"}
              </button>
            </div>
          </section>

          {/* Spacer for sticky bar */}
          <div className="h-16" />

          {/* Sticky save bar */}
          <div className="fixed bottom-4 left-0 right-0 px-4">
            <div className="max-w-7xl mx-auto bg-white border border-gray-200 shadow-xl rounded-xl p-3 flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {selectedId
                  ? `Selected: ${selectedId}`
                  : mode === "create"
                  ? "Creating new datasheet"
                  : "No datasheet selected"}
                {form.code && ` · Code ${form.code}`}
                {form.category && ` · ${form.category}`}
              </span>

              {infoMessage && (
                <span className="text-sm text-green-600">{infoMessage}</span>
              )}
              {saveError && (
                <span className="text-sm text-red-600">{saveError}</span>
              )}

              <button
                type="button"
                onClick={startNew}
                className="ml-auto px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
              >
                Reset form
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`px-6 py-2 rounded-md text-white text-sm transition ${
                  isSaving
                    ? "bg-indigo-300 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {isSaving ? "Saving…" : "Save datasheet"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
